'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var _extends = require('@babel/runtime/helpers/extends');
var React = require('react');
var nthHookTranslations = require('@narvar/nth-hook-translations');
var nthHookTheme = require('@narvar/nth-hook-theme');
var nthHookLoma = require('@narvar/nth-hook-loma');
var nthHookContextPage = require('@narvar/nth-hook-context-page');
var Panel = require('@narvar/nth-block-panel');
var Spinner = require('@narvar/nth-bit-icon-spinner');
var useOrderTracking = require('@narvar/nth-hook-order-tracking');
var ResizableMap = require('./ResizableMap.js');
var PrefetchData = require('./PrefetchData.js');
var LocalDeliveryMap_module = require('./LocalDeliveryMap.module.css.js');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var _extends__default = /*#__PURE__*/_interopDefaultLegacy(_extends);
var React__default = /*#__PURE__*/_interopDefaultLegacy(React);
var Panel__default = /*#__PURE__*/_interopDefaultLegacy(Panel);
var Spinner__default = /*#__PURE__*/_interopDefaultLegacy(Spinner);
var useOrderTracking__default = /*#__PURE__*/_interopDefaultLegacy(useOrderTracking);

const FETCH_COURIER_LOCATION_POLL_INTERVAL = 30000;
const LocalDeliveryMap = ({
  featureName,
  fixedPanelHeight
}) => {
  const {
    spinnerPrimaryNone
  } = nthHookTheme.useTheme();
  const {
    state: {
      carrier,
      retailerMoniker,
      trackingNumbers,
      orderNumbers
    }
  } = nthHookContextPage.usePageContext();
  const {
    getCurrentShipment
  } = useOrderTracking__default["default"]();
  const currentShipment = getCurrentShipment();
  const maptiler = nthHookLoma.useMaptilerQuery();
  const trackingNumber = trackingNumbers?.[0];
  const {
    data: courierLocationData
  } = nthHookLoma.useCourierLocationQuery({
    variables: {
      courier: carrier ?? '',
      trackingNumber: trackingNumber ?? '',
      limit: 1
    },
    pollInterval: FETCH_COURIER_LOCATION_POLL_INTERVAL,
    skip: typeof trackingNumber !== 'string' || !currentShipment
  });
  if (maptiler.loading) {
    return /*#__PURE__*/React__default["default"].createElement("div", {
      className: LocalDeliveryMap_module["default"].loadingContainer
    }, /*#__PURE__*/React__default["default"].createElement(Spinner__default["default"], {
      s: spinnerPrimaryNone
    }));
  }
  const maptilerKey = maptiler.data?.maptiler?.key;
  if (!currentShipment || maptilerKey === undefined) {
    return /*#__PURE__*/React__default["default"].createElement(React__default["default"].Fragment, null);
  }
  const {
    status,
    statusCode,
    lastUpdate
  } = currentShipment;
  if (status === 'HPUPICKEDUP') {
    return /*#__PURE__*/React__default["default"].createElement(React__default["default"].Fragment, null);
  }
  const isHPU = ['HPUSCHEDULED', 'HPUEXCEPTION', 'HPUPICKUPINPROGRESS'].includes(status ?? '');
  const isMapview = ['MVINTRANSIT', 'MVDELIVERED', 'MVEXCEPTION'].includes(status ?? '');
  const showOrigin = ['SHPD_SDD_101', 'SHPD_SDD_201', 'SHPD_SDD_280', 'SHPD_SDD_281', 'EXCP_SDD_800'].includes(statusCode ?? '') || isMapview;
  const {
    origin
  } = currentShipment;
  const originLng = showOrigin && typeof origin?.lng === 'string' ? +origin.lng : undefined;
  const originLat = showOrigin && typeof origin?.lat === 'string' ? +origin.lat : undefined;
  const {
    destination
  } = currentShipment;
  const destinationLng = !isHPU && typeof destination?.lng === 'string' ? +destination.lng : undefined;
  const destinationLat = !isHPU && typeof destination?.lat === 'string' ? +destination.lat : undefined;
  const showCourier = ['SHPD_SDD_280', 'SHPD_SDD_281', 'INTR_SDD_311', 'OFDL_SDD_400', 'OFDL_SDD_403', 'DLVD_SDD_500'].includes(statusCode ?? '') || isMapview;
  const courierLocationEvents = courierLocationData?.courierLocation?.courierLocationEvents;
  const courierLng = showCourier ? courierLocationEvents && courierLocationEvents[0]?.longitude : undefined;
  const courierLat = showCourier ? courierLocationEvents && courierLocationEvents[0]?.latitude : undefined;

  // show map if any set of coords is available
  const hasAnyLocation = originLng && originLat || destinationLng && destinationLat || courierLng && courierLat;
  const height = fixedPanelHeight ?? (hasAnyLocation ? '280px' : '0px');
  if (showCourier && courierLocationEvents?.length === 0) {
    // We are supposed to show courier icon but no location coordinates
    // eslint-disable-next-line no-console
    console.error('Local-Delivery-Map: We are not getting any courier location ', {
      retailerMoniker,
      trackingNumbers,
      orderNumbers,
      courierLocation: courierLocationData
    });
  }
  const timestamp = [(courierLocationEvents && courierLocationEvents[0]?.eventTimestamp) ?? '', lastUpdate ?? ''].sort()[1];
  let lastUpdated;
  if (timestamp) {
    const time = new Date(timestamp).toLocaleString('en-US', {
      hour: 'numeric',
      hour12: true,
      minute: 'numeric'
    });
    lastUpdated = `Last updated ${time}`;
  } else {
    lastUpdated = 'Last updated: Unavailable';
  }
  return /*#__PURE__*/React__default["default"].createElement(Panel__default["default"], {
    featureName: featureName,
    fixedPanelHeight: height,
    sOuter: {
      border: 'none'
    },
    sInner: {
      padding: 0
    },
    header: /*#__PURE__*/React__default["default"].createElement(React__default["default"].Fragment, null)
  }, hasAnyLocation && /*#__PURE__*/React__default["default"].createElement(ResizableMap["default"], {
    maptilerKey: maptilerKey,
    originLng: originLng,
    originLat: originLat,
    destinationLng: destinationLng,
    destinationLat: destinationLat,
    courierLng: courierLng,
    courierLat: courierLat,
    lastUpdated: lastUpdated,
    featureName: featureName,
    isHPU: isHPU
  }));
};
const LocalDeliveryMapWithData = nthHookTranslations.withTranslationsProvider(nthHookTheme.withThemeProvider(LocalDeliveryMap, {
  themeTokens: ['panelcontainerNoneNone', 'spinnerPrimaryNone']
}), {
  translationKeys: ['deliveryMapRefreshText']
});
const LocalDeliveryMapWithPrefetchedData = ({
  featureName,
  ...props
}) => /*#__PURE__*/React__default["default"].createElement(PrefetchData["default"], {
  featureName: featureName
}, /*#__PURE__*/React__default["default"].createElement(LocalDeliveryMapWithData, _extends__default["default"]({
  featureName: featureName
}, props)));

exports["default"] = LocalDeliveryMapWithPrefetchedData;
